/**
 * @namespace DklEvents
 * @description Discount Kit specific events used by custom widgets.
 *
 * All event names follow the `discount-kit:[action]` pattern.
 */
export class DklEvents {
  /** @static @constant {string} Fired when any Discount Kit widget is mounted */
  static widgetMount = 'discount-kit:widget:mount';

  /** @static @constant {string} Fired when any Discount Kit widget is unmounted */
  static widgetUnmount = 'discount-kit:widget:unmount';

  /**
   * @static @constant {string}
   * Fired when a volume discount tier is changed within a widget.
   */
  static volumeDiscountTierChange = 'discount-kit:volume-discount:tier-change';

  /**
   * @static @constant {string}
   * Fired when an order goal tier is met/changed within a widget.
   */
  static orderGoalTierChange = 'discount-kit:order-goal:tier-change';

  /**
   * @static @constant {string}
   * Fired when a GWP tier is met/changed within a widget.
   */
  static gwpTierChange = 'discount-kit:gwp:tier-change';

  /**
   * @static @constant {string}
   * Fired when a GWP gift is added to the cart.
   */
  static gwpGiftAdded = 'discount-kit:gwp:gift-added';

  /**
   * @static @constant {string}
   * Fired when a GWP gift is removed from the cart.
   */
  static gwpGiftRemoved = 'discount-kit:gwp:gift-removed';

  /**
   * @static @constant {string}
   * Fired when a GWP gift is swapped (removed and replaced with another).
   */
  static gwpGiftSwapped = 'discount-kit:gwp:gift-swapped';
}

/**
 * @typedef {Object} DklWidgetEventDetail
 * @property {string} widgetType - Type/name of the widget (e.g. 'volume-picker-radio-groups').
 * @property {number | null} [productId] - Shopify product ID, if available.
 * @property {number | null} [variantId] - Shopify variant ID, if available.
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 */

/**
 * Event fired when a Discount Kit widget is mounted.
 * @extends {Event}
 */
export class DklWidgetMountEvent extends Event {
  /**
   * @param {DklWidgetEventDetail} resource
   */
  constructor(resource) {
    super(DklEvents.widgetMount, { bubbles: true });
    /** @type {{ resource: DklWidgetEventDetail }} */
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired when a Discount Kit widget is unmounted.
 * @extends {Event}
 */
export class DklWidgetUnmountEvent extends Event {
  /**
   * @param {DklWidgetEventDetail} resource
   */
  constructor(resource) {
    super(DklEvents.widgetUnmount, { bubbles: true });
    /** @type {{ resource: DklWidgetEventDetail }} */
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} VolumeDiscountTier
 * @property {number} index - Zero-based index of the tier.
 * @property {number} quantity - Minimum quantity for the tier.
 * @property {number} discountAmount - The discount amount:
 *   - For `percentage` discounts: the percent value (for example, 20 for 20%).
 *   - For `fixedAmount` discounts: the money amount in major units (for example, 0.5 for £0.50).
 * @property {string} discountType - The discount type (for example, 'percentage' or 'fixedAmount').
 * @property {number} unitPriceCents - Discounted price per unit in cents.
 */

/**
 * @typedef {Object} VolumeDiscountTierChangeResource
 * @property {number | null} productId - Shopify product ID, if available.
 * @property {number | null} variantId - Shopify variant ID, if available.
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 * @property {string} widgetType - Widget type identifier (for example, 'volume-picker-radio-groups').
 * @property {VolumeDiscountTier | null} previousTier - The previously selected tier, if any.
 * @property {VolumeDiscountTier} currentTier - The currently selected tier.
 */

/**
 * Event fired when a volume discount tier is changed.
 * @extends {Event}
 */
export class VolumeDiscountTierChangeEvent extends Event {
  /**
   * @param {VolumeDiscountTierChangeResource} resource
   */
  constructor(resource) {
    super(DklEvents.volumeDiscountTierChange, { bubbles: true });
    /** @type {{ resource: VolumeDiscountTierChangeResource }} */
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} OrderGoalTier
 * @property {number} tierLevel - 1-based tier level (tier 1, tier 2, etc.).
 * @property {number} minSpendCents - Minimum spend (in cents) required for the tier.
 * @property {number} discountAmount - The raw discount amount from the config.
 * @property {string} discountType - The discount type (for example, 'percentage' or 'fixedAmount').
 * @property {string} message - The display message associated with the tier.
 */

/**
 * @typedef {Object} OrderGoalTierChangeResource
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 * @property {string} widgetType - Widget type identifier (for example, 'order-goal-cart').
 * @property {OrderGoalTier | null} previousTier - The previously met tier, if any.
 * @property {OrderGoalTier | null} currentTier - The currently met tier, if any.
 * @property {number} cartTotalCents - Current cart total in cents.
 */

/**
 * Event fired when an order goal tier is changed.
 * @extends {Event}
 */
export class OrderGoalTierChangeEvent extends Event {
  /**
   * @param {OrderGoalTierChangeResource} resource
   */
  constructor(resource) {
    super(DklEvents.orderGoalTierChange, { bubbles: true });
    /** @type {{ resource: OrderGoalTierChangeResource }} */
    this.detail = {
      resource,
    };
  }
}


/**
 * @typedef {Object} GWPTier
 * @property {number} tierLevel - 0-based tier level index matching discount configuration.
 * @property {number} threshold - Threshold value for the tier (cents or quantity).
 * @property {string} buysType - The condition type ('minimumQuantity' or 'minimumSubtotal').
 * @property {string} message - The display message associated with the tier.
 */

/**
 * @typedef {Object} GWPTierChangeResource
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 * @property {string} widgetType - Widget type identifier (for example, 'gwp').
 * @property {GWPTier | null} previousTier - The previously met tier, if any.
 * @property {GWPTier | null} currentTier - The currently met tier, if any.
 * @property {number} cartCurrentValue - Current cart value (subtotal in cents or item count).
 */

/**
 * Event fired when a GWP tier is changed.
 * @extends {Event}
 */
export class GWPTierChangeEvent extends Event {
  /**
   * @param {GWPTierChangeResource} resource
   */
  constructor(resource) {
    super(DklEvents.gwpTierChange, { bubbles: true });
    /** @type {{ resource: GWPTierChangeResource }} */
    this.detail = {
      resource, 
    };
  }
}

/**
 * @typedef {Object} GWPVariant
 * @property {number} variantId - The Shopify variant ID.
 * @property {number} productId - The Shopify product ID.
 */

/**
 * @typedef {Object} GWPGiftResource
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 * @property {string} widgetType - Widget type identifier (for example, 'gwp').
 * @property {number} tierIndex - The tier index (0-based) that this gift belongs to.
 * @property {string} tierIdentifier - The tier identifier string (e.g., 'DiscountTitle_tier_0').
 * @property {GWPVariant} variant - The gift variant information.
 * @property {string} discountTitle - The title of the GWP discount.
 * @property {number} cartCurrentValue - Current cart value (subtotal in cents or item count).
 */

/**
 * Event fired when a GWP gift is added to the cart.
 * @extends {Event}
 */
export class GWPGiftAddedEvent extends Event {
  /**
   * @param {GWPGiftResource} resource
   */
  constructor(resource) {
    super(DklEvents.gwpGiftAdded, { bubbles: true });
    /** @type {{ resource: GWPGiftResource }} */
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired when a GWP gift is removed from the cart.
 * @extends {Event}
 */
export class GWPGiftRemovedEvent extends Event {
  /**
   * @param {GWPGiftResource} resource
   */
  constructor(resource) {
    super(DklEvents.gwpGiftRemoved, { bubbles: true });
    /** @type {{ resource: GWPGiftResource }} */
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} GWPGiftSwappedResource
 * @property {string | null} widgetId - ID of the Discount Kit widget instance.
 * @property {string} widgetType - Widget type identifier (for example, 'gwp').
 * @property {number} tierIndex - The tier index (0-based) that this gift belongs to.
 * @property {string} tierIdentifier - The tier identifier string (e.g., 'DiscountTitle_tier_0').
 * @property {GWPVariant} oldVariant - The gift variant being removed.
 * @property {GWPVariant} newVariant - The gift variant being added.
 * @property {string} discountTitle - The title of the GWP discount.
 * @property {number} cartCurrentValue - Current cart value (subtotal in cents or item count).
 */

/**
 * Event fired when a GWP gift is swapped (removed and replaced with another).
 * @extends {Event}
 */
export class GWPGiftSwappedEvent extends Event {
  /**
   * @param {GWPGiftSwappedResource} resource
   */
  constructor(resource) {
    super(DklEvents.gwpGiftSwapped, { bubbles: true });
    /** @type {{ resource: GWPGiftSwappedResource }} */
    this.detail = {
      resource,
    };
  }
}